/* One interactive page, so one store: which chapter is open, which card is
 * igniting, what the toast and the live region say, and every action that
 * changes progress.
 *
 * The artifact dispatched all of this from a single document-level click
 * listener reading `data-act` attributes. The attributes are still in the
 * markup (they are part of the design contract), but actions are typed
 * functions now, and nothing reaches into the DOM to re-render.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { byId, chapterDone, checkIndex, curBlock, dueList, firstUndone, firstOpen, srsOf, unlockNeighbours, xpOf } from '../engine/progress';
import { nextInterval, pRecall, restartedInterval } from '../engine/srs';
import { gNew } from '../engine/game';
import { scrollToReader } from '../engine/scroll';
import {
  markDone as markBlockDoneInStore,
  resetAll,
  setChecked,
  setCursor,
  setGame,
  setReview,
  useProgress,
} from '../engine/store';
import { SRS } from '../data/srs';
import type { Block, NodeDef, SavedGame, SavedState } from '../types';

const IGNITE_MS = 900;
const TOAST_MS = 3600;

export interface AppApi {
  progress: SavedState;
  /** the open chapter, or null for the map */
  sel: string | null;
  /** the chapter card currently lifting, one-shot after a chapter clears */
  ignite: string | null;
  toast: { message: string; visible: boolean };
  live: string;
  about: { open: boolean; show: () => void; hide: () => void };
  resetConfirm: { open: boolean; ask: () => void; cancel: () => void };
  exerciseRound: Record<string, number>;
  announce: (message: string) => void;
  notify: (message: string) => void;
  selectNode: (id: string | null) => void;
  startReview: (id: string) => void;
  goBlock: (nodeId: string, index: number) => void;
  nextBlock: (nodeId: string) => void;
  prevBlock: (nodeId: string) => void;
  finishChapter: (nodeId: string) => void;
  passCheck: (node: NodeDef, block: Block) => void;
  missCheck: (nodeId: string) => void;
  replayCheck: (blockId: string) => void;
  completeBlock: (blockId: string) => void;
  resetProgress: () => void;
  setGameState: (game: SavedGame | null) => void;
}

const AppContext = createContext<AppApi | null>(null);

export function useApp(): AppApi {
  const api = useContext(AppContext);
  if (!api) throw new Error('useApp must be used inside <AppProvider>');
  return api;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const progress = useProgress();
  const { t } = useTranslation();

  const [sel, setSel] = useState<string | null>(null);
  const [ignite, setIgnite] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [live, setLive] = useState('');
  const [aboutOpen, setAboutOpen] = useState(false);
  const [resetAskOpen, setResetAskOpen] = useState(false);
  const [exerciseRound, setExerciseRound] = useState<Record<string, number>>({});

  const pendingIgnite = useRef<string | null>(null);
  const igniteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* A chapter is read as a document: opening one, and stepping inside one,
     must land on the reader rather than mid-page. */
  useEffect(() => {
    if (!sel) return;
    const frame = requestAnimationFrame(scrollToReader);
    return () => cancelAnimationFrame(frame);
  }, [sel]);

  useEffect(
    () => () => {
      if (igniteTimer.current) clearTimeout(igniteTimer.current);
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const notify = useCallback((text: string) => {
    setMessage(text);
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), TOAST_MS);
  }, []);

  const announce = useCallback((text: string) => setLive(text), []);

  const playIgnite = useCallback((id: string) => {
    pendingIgnite.current = null;
    setIgnite(id);
    if (igniteTimer.current) clearTimeout(igniteTimer.current);
    igniteTimer.current = setTimeout(() => setIgnite(null), IGNITE_MS);
  }, []);

  /* The card lifts once, on the chapter just cleared — but a check is passed
     inside the lesson view, where the map is not on screen. The ignition is
     held and plays the next time the map is actually in front of you. */
  const igniteChapter = useCallback(
    (node: NodeDef) => {
      if (sel) pendingIgnite.current = node.id;
      else playIgnite(node.id);
    },
    [playIgnite, sel],
  );

  const selectNode = useCallback(
    (id: string | null) => {
      if (!id) {
        setSel(null);
        setResetAskOpen(false);
        if (pendingIgnite.current) playIgnite(pendingIgnite.current);
        return;
      }
      const node = byId(id);
      if (!node) return;
      /* Prerequisites are advice. The map marks a chapter "later in the path" and
         says what it assumes; it never blocks a reader who chose it — otherwise
         "enter by subject" would be a lie. */
      setResetAskOpen(false);
      setSel(id);
      if (typeof progress.cur[id] !== 'number') setCursor(id, firstUndone(node, progress));
    },
    [playIgnite, progress],
  );

  const goBlock = useCallback(
    (nodeId: string, index: number) => {
      const node = byId(nodeId);
      if (!node) return;
      const clamped = Math.max(0, Math.min(node.blocks.length - 1, index));
      setCursor(nodeId, clamped);
      if (sel) requestAnimationFrame(scrollToReader);
    },
    [sel],
  );

  const nextBlock = useCallback(
    (nodeId: string) => {
      const node = byId(nodeId);
      if (!node) return;
      const index = curBlock(node, progress);
      const block = node.blocks[index];
      if (block.kind !== 'check') markBlockDoneInStore(block.id);
      goBlock(nodeId, index + 1);
    },
    [goBlock, progress],
  );

  const prevBlock = useCallback(
    (nodeId: string) => {
      const node = byId(nodeId);
      if (!node) return;
      goBlock(nodeId, curBlock(node, progress) - 1);
    },
    [goBlock, progress],
  );

  const completeBlock = useCallback((blockId: string) => {
    markBlockDoneInStore(blockId);
  }, []);

  const passCheck = useCallback(
    (node: NodeDef, block: Block) => {
      const before = progress;
      const row = srsOf(node.id, before);
      const wasDue = !!row && pRecall(row) < SRS.TARGET;
      const wasDone = chapterDone(node, before);

      const after: SavedState = { ...before, chk: { ...before.chk, [block.id]: true } };

      setChecked(block.id, true);
      setReview(node.id, nextInterval(before.rev[node.id]));

      if (!wasDone && chapterDone(node, after)) igniteChapter(node);
      if (chapterDone(node, after)) notify(`${t('doneToast')} · ${xpOf(node)} ${t('xp')}`);
      else if (wasDue) notify(t('reviewPassed'));
      else notify(`${t('passedToast')} · ${t('xp')} ${block.xp}`);
    },
    [igniteChapter, notify, progress, t],
  );

  const missCheck = useCallback(
    (nodeId: string) => {
      const row = srsOf(nodeId, progress);
      if (row && pRecall(row) < SRS.TARGET) setReview(nodeId, restartedInterval());
    },
    [progress],
  );

  const replayCheck = useCallback((blockId: string) => {
    setChecked(blockId, false);
    setExerciseRound((rounds) => ({ ...rounds, [blockId]: (rounds[blockId] ?? 1) + 1 }));
  }, []);

  const startReview = useCallback(
    (id: string) => {
      const node = byId(id);
      if (!node) return;
      selectNode(id);
      goBlock(id, checkIndex(node));
      const row = srsOf(id, progress);
      notify(`${t('reviewStart')} · ${Math.round(pRecall(row) * 100)}%`);
    },
    [goBlock, notify, progress, selectNode, t],
  );

  const finishChapter = useCallback(
    (nodeId: string) => {
      const node = byId(nodeId);
      if (!node) return;
      const index = curBlock(node, progress);
      const block = node.blocks[index];
      if (block.kind !== 'check' || !progress.chk[block.id]) return;
      const next = unlockNeighbours(node, progress);
      if (next.length) selectNode(next[0].id);
    },
    [progress, selectNode],
  );

  const resetProgress = useCallback(() => {
    resetAll();
    setGame(null);
    setSel(null);
    setResetAskOpen(false);
    setExerciseRound({});
    notify(t('savedToast'));
  }, [notify, t]);

  const api = useMemo<AppApi>(
    () => ({
      progress,
      sel,
      ignite,
      toast: { message, visible: toastVisible },
      live,
      about: { open: aboutOpen, show: () => setAboutOpen(true), hide: () => setAboutOpen(false) },
      resetConfirm: { open: resetAskOpen, ask: () => setResetAskOpen(true), cancel: () => setResetAskOpen(false) },
      exerciseRound,
      announce,
      notify,
      selectNode,
      startReview,
      goBlock,
      nextBlock,
      prevBlock,
      finishChapter,
      passCheck,
      missCheck,
      replayCheck,
      completeBlock,
      resetProgress,
      setGameState: setGame,
    }),
    [
      aboutOpen,
      announce,
      completeBlock,
      exerciseRound,
      finishChapter,
      goBlock,
      ignite,
      live,
      message,
      missCheck,
      nextBlock,
      notify,
      passCheck,
      prevBlock,
      progress,
      replayCheck,
      resetAskOpen,
      resetProgress,
      sel,
      selectNode,
      startReview,
      toastVisible,
    ],
  );

  return <AppContext.Provider value={api}>{children}</AppContext.Provider>;
}

/* Convenience selectors used by more than one component. */
export function useFirstOpen(): NodeDef {
  const { progress } = useApp();
  return useMemo(() => firstOpen(progress), [progress]);
}

export function useDue(): NodeDef[] {
  const { progress } = useApp();
  return useMemo(() => dueList(progress), [progress]);
}

export function useBonus(): NodeDef | null {
  return useMemo(() => byId('bonus-pipeline'), []);
}

/* The board is created lazily: a learner who never opens the bonus stage never
   pays for a random board. */
export function useNewBoard(): number[] {
  return useMemo(() => gNew(), []);
}
